// The terms text is stored as a small markdown subset (MegaPayBackend Services/Terms/Versions):
//   "## title"             a section heading
//   "- item" lines         one list
//   any other line         a paragraph
//   **bold** and [text](https://…) inside a line
// Blocks are separated by blank lines. Everything is rendered as React text, never as HTML, so the
// text cannot inject markup; links other than https:// stay plain text.

export type TermsInline =
  | {type: 'text'; text: string}
  | {type: 'strong'; text: string}
  | {type: 'link'; text: string; href: string}

export type TermsBlock =
  | {type: 'heading'; inlines: TermsInline[]}
  | {type: 'paragraph'; inlines: TermsInline[]}
  | {type: 'list'; items: TermsInline[][]}

const INLINE_PATTERN = '\\*\\*(.+?)\\*\\*|\\[([^\\]]+)\\]\\(([^)\\s]+)\\)'

export const parseTermsInline = (line: string): TermsInline[] => {
  const inlines: TermsInline[] = []
  const pattern = new RegExp(INLINE_PATTERN, 'g')
  let position = 0
  let match: RegExpExecArray | null

  const pushText = (text: string) => {
    if (!text) return
    const last = inlines[inlines.length - 1]
    if (last?.type === 'text') last.text += text
    else inlines.push({type: 'text', text})
  }

  while ((match = pattern.exec(line)) !== null) {
    const index = match.index
    pushText(line.slice(position, index))
    if (match[1] !== undefined) {
      inlines.push({type: 'strong', text: match[1]})
    } else if (/^https:\/\/[^\s]+$/i.test(match[3])) {
      inlines.push({type: 'link', text: match[2], href: match[3]})
    } else {
      pushText(match[2])
    }
    position = index + match[0].length
  }

  pushText(line.slice(position))
  return inlines
}

export const parseTermsMarkdown = (content: string): TermsBlock[] => {
  const blocks: TermsBlock[] = []
  const chunks = String(content || '')
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .map((chunk) => chunk.trim())
    .filter(Boolean)

  for (const chunk of chunks) {
    const lines = chunk.split('\n').map((line) => line.trim())
    if (lines.length === 1 && lines[0].startsWith('## ')) {
      blocks.push({type: 'heading', inlines: parseTermsInline(lines[0].slice(3).trim())})
    } else if (lines.every((line) => line.startsWith('- '))) {
      blocks.push({
        type: 'list',
        items: lines.map((line) => parseTermsInline(line.slice(2).trim())),
      })
    } else {
      blocks.push({type: 'paragraph', inlines: parseTermsInline(lines.join(' '))})
    }
  }

  return blocks
}
